/**
 * Click-Level Detail Reports — Trackier-style operator report (FS-297 / row 297).
 *
 * Layout (top-to-bottom):
 *   1. Topbar   — title, date-range picker, Export button.
 *   2. Tiles    — Total clicks, Today, Last 7d, Unique IPs.
 *   3. Chart    — inline SVG line-chart of clicks/day across the range.
 *   4. Filters  — Campaign, Country (debounced submit).
 *   5. Table    — ts, ip, country, ua, campaign, referrer drilldown.
 *
 * Brand: green-600/700/50 accent only; white / zinc-200 / zinc-900 surfaces.
 * No emoji, no dark theme, no competitor wording.
 *
 * Backend contract: GET /api/v1/click-level-detail-reports/clicks returns
 * `{ data: ClickDetailView[], meta: { per_page, total, next_cursor } }`.
 * We read only `data` here — pagination is driven via "Load more" server-side
 * in a future iteration; the initial page sizes at per_page=500 which is the
 * server-side cap and covers most operator date-range slices.
 */

import { useMemo, useState } from "react";
import {
	daysAgoSec,
	formatUtcTs,
	localDateFromSec,
	nowSec,
	secFromLocalDate,
	truncate,
	uniqueIps,
	countSince,
	dailySeries,
	type ClickDetailView,
	type ListClicksFilters,
	type SeriesPoint,
} from "./api";
import {
	useClickLevelDetailReportsQuery,
	useClickLevelDetailReportsStats,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — inlined per owned-file constraint.
// ---------------------------------------------------------------------------

const CARD = "rounded-lg border border-zinc-200 bg-white shadow-sm";
const LABEL =
	"block text-xs font-medium uppercase tracking-wider text-zinc-500";
const INPUT =
	"w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Inline SVG icons — stroke-current so they inherit colour.
// ---------------------------------------------------------------------------

function IconDownload({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<polyline points="7 10 12 15 17 10" />
			<line x1="12" y1="15" x2="12" y2="3" />
		</svg>
	);
}

function IconCalendar({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
			<line x1="16" y1="2" x2="16" y2="6" />
			<line x1="8" y1="2" x2="8" y2="6" />
			<line x1="3" y1="10" x2="21" y2="10" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Date-range state.
// ---------------------------------------------------------------------------

interface DateRange {
	sinceDate: string; // YYYY-MM-DD (date input)
	untilDate: string;
}

function initialRange(): DateRange {
	return {
		sinceDate: localDateFromSec(daysAgoSec(7)),
		untilDate: localDateFromSec(nowSec()),
	};
}

function rangeToFilters(
	r: DateRange,
	campaign: string,
	country: string,
): ListClicksFilters {
	const sinceSec = secFromLocalDate(r.sinceDate);
	// "until" is end of day (inclusive).
	const untilBase = secFromLocalDate(r.untilDate);
	const untilSec = untilBase !== null ? untilBase + 86399 : null;
	return {
		since: sinceSec,
		until: untilSec,
		campaign_id: campaign.trim() || null,
		country: country.trim() || null,
		per_page: 500,
	};
}

// ---------------------------------------------------------------------------
// CSV export — uses the already-fetched rows so no extra fetch() needed here
// (keeps raw fetch calls in api.ts only).
// ---------------------------------------------------------------------------

function exportRowsToCsv(rows: ClickDetailView[]): void {
	const cols: (keyof ClickDetailView)[] = [
		"ts",
		"click_id",
		"ip",
		"country",
		"ua",
		"device_type",
		"campaign_id",
		"affiliate_id",
		"referrer",
		"landing_url",
		"status",
	];
	const esc = (v: unknown) => {
		if (v === null || v === undefined) return "";
		const s = String(v);
		return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
	};
	const header = cols.join(",") + "\n";
	const body = rows
		.map((r) => cols.map((c) => esc(r[c])).join(","))
		.join("\n");
	const blob = new Blob([header + body + "\n"], {
		type: "text/csv;charset=utf-8",
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = "click-level-detail-reports.csv";
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// Metric tile.
// ---------------------------------------------------------------------------

function Tile({
	label,
	value,
	testId,
}: {
	label: string;
	value: string | number;
	testId: string;
}) {
	return (
		<div className={`${CARD} px-5 py-4`} data-testid={testId}>
			<p className={LABEL}>{label}</p>
			<p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
				{typeof value === "number" ? value.toLocaleString() : value}
			</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sparkline — pure SVG line-chart of clicks per day.
// ---------------------------------------------------------------------------

function Sparkline({ points }: { points: SeriesPoint[] }) {
	if (points.length === 0) return null;
	const W = 720;
	const H = 160;
	const PAD_L = 36;
	const PAD_R = 12;
	const PAD_T = 16;
	const PAD_B = 24;
	const innerW = W - PAD_L - PAD_R;
	const innerH = H - PAD_T - PAD_B;

	const maxY = Math.max(1, ...points.map((p) => p.count));
	const stepX = points.length > 1 ? innerW / (points.length - 1) : innerW;
	const xAt = (i: number) => PAD_L + i * stepX;
	const yAt = (v: number) => PAD_T + innerH - (v / maxY) * innerH;

	const path = points
		.map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)},${yAt(p.count).toFixed(1)}`)
		.join(" ");

	const areaPath =
		path +
		` L${xAt(points.length - 1).toFixed(1)},${(PAD_T + innerH).toFixed(1)}` +
		` L${xAt(0).toFixed(1)},${(PAD_T + innerH).toFixed(1)} Z`;

	// Axis ticks — show up to 5 x-labels (first/mid/last sample).
	const tickIdxs = (() => {
		if (points.length <= 5) return points.map((_, i) => i);
		const step = (points.length - 1) / 4;
		return [0, 1, 2, 3, 4].map((k) => Math.round(k * step));
	})();

	return (
		<svg
			viewBox={`0 0 ${W} ${H}`}
			className="h-40 w-full"
			data-testid="click-level-detail-reports-chart"
			role="img"
			aria-label="Clicks per day over the selected range"
		>
			<title>Clicks per day</title>
			{/* horizontal grid */}
			{[0.25, 0.5, 0.75, 1].map((f) => {
				const y = PAD_T + innerH - f * innerH;
				return (
					<line
						key={`grid-${f}`}
						x1={PAD_L}
						x2={W - PAD_R}
						y1={y}
						y2={y}
						stroke="#e4e4e7"
						strokeWidth={1}
					/>
				);
			})}
			{/* y-axis max label */}
			<text
				x={PAD_L - 6}
				y={PAD_T + 4}
				textAnchor="end"
				className="fill-zinc-500"
				style={{ fontSize: 10 }}
			>
				{maxY.toLocaleString()}
			</text>
			<text
				x={PAD_L - 6}
				y={PAD_T + innerH + 4}
				textAnchor="end"
				className="fill-zinc-500"
				style={{ fontSize: 10 }}
			>
				0
			</text>
			<path
				d={areaPath}
				fill="rgb(22 163 74 / 0.08)"
				stroke="none"
			/>
			<path
				d={path}
				fill="none"
				stroke="rgb(22 163 74)"
				strokeWidth={2}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			{points.map((p, i) => (
				<circle
					key={`pt-${p.day}`}
					cx={xAt(i)}
					cy={yAt(p.count)}
					r={2}
					fill="rgb(22 163 74)"
				/>
			))}
			{tickIdxs.map((i) => (
				<text
					key={`tk-${i}`}
					x={xAt(i)}
					y={H - 6}
					textAnchor="middle"
					className="fill-zinc-500"
					style={{ fontSize: 10 }}
				>
					{localDateFromSec(points[i].day).slice(5)}
				</text>
			))}
		</svg>
	);
}

// ---------------------------------------------------------------------------
// ClickLevelDetailReportsPage.
// ---------------------------------------------------------------------------

export function ClickLevelDetailReportsPage() {
	const [range, setRange] = useState<DateRange>(initialRange);
	const [campaign, setCampaign] = useState("");
	const [country, setCountry] = useState("");
	const [applied, setApplied] = useState<ListClicksFilters>(() =>
		rangeToFilters(initialRange(), "", ""),
	);

	const listQuery = useClickLevelDetailReportsQuery(applied);
	const statsQuery = useClickLevelDetailReportsStats();

	const rows = useMemo<ClickDetailView[]>(
		() => listQuery.data?.data ?? [],
		[listQuery.data],
	);

	const todayStart = useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		return Math.floor(d.getTime() / 1000);
	}, []);

	const totalClicks = statsQuery.data?.total ?? rows.length;
	const todayCount = countSince(rows, todayStart);
	const last7dCount = countSince(rows, daysAgoSec(7));
	const uniqueIpCount = uniqueIps(rows);

	const sinceSec = applied.since ?? daysAgoSec(7);
	const untilSec = applied.until ?? nowSec();
	const series = useMemo(
		() => dailySeries(rows, sinceSec, untilSec),
		[rows, sinceSec, untilSec],
	);

	const applyFilters = (e?: React.FormEvent<HTMLFormElement>) => {
		e?.preventDefault();
		setApplied(rangeToFilters(range, campaign, country));
	};

	const handleExport = () => {
		if (rows.length === 0) return;
		exportRowsToCsv(rows);
	};

	const handleReset = () => {
		const fresh = initialRange();
		setRange(fresh);
		setCampaign("");
		setCountry("");
		setApplied(rangeToFilters(fresh, "", ""));
	};

	return (
		<div
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6"
			data-testid="click-level-detail-reports-page"
		>
			{/* Topbar */}
			<header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Report
					</p>
					<h1 className="text-2xl font-semibold text-zinc-900">
						Click-Level Detail Reports
					</h1>
					<p className="text-sm text-zinc-500">
						Per-click log across campaigns, countries, and device types. Drill
						down into any click to reconcile payouts or investigate fraud.
					</p>
				</div>
				<div className="flex flex-wrap items-end gap-3">
					<div className="flex items-end gap-2">
						<div>
							<label htmlFor="clrd-since" className={LABEL}>
								<span className="inline-flex items-center gap-1.5">
									<IconCalendar className="text-zinc-500" />
									From
								</span>
							</label>
							<input
								id="clrd-since"
								type="date"
								className={INPUT}
								value={range.sinceDate}
								onChange={(e) =>
									setRange((r) => ({ ...r, sinceDate: e.target.value }))
								}
								data-testid="click-level-detail-reports-since"
							/>
						</div>
						<div>
							<label htmlFor="clrd-until" className={LABEL}>
								Until
							</label>
							<input
								id="clrd-until"
								type="date"
								className={INPUT}
								value={range.untilDate}
								onChange={(e) =>
									setRange((r) => ({ ...r, untilDate: e.target.value }))
								}
								data-testid="click-level-detail-reports-until"
							/>
						</div>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => applyFilters()}
							data-testid="click-level-detail-reports-apply-range"
						>
							Apply
						</button>
					</div>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={handleExport}
						disabled={rows.length === 0}
						data-testid="click-level-detail-reports-export"
					>
						<IconDownload className="text-white" />
						Export CSV
					</button>
				</div>
			</header>

			{/* Tiles */}
			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
				aria-label="Summary metrics"
			>
				<Tile
					label="Total clicks"
					value={totalClicks}
					testId="click-level-detail-reports-tile-total"
				/>
				<Tile
					label="Today"
					value={todayCount}
					testId="click-level-detail-reports-tile-today"
				/>
				<Tile
					label="Last 7d"
					value={last7dCount}
					testId="click-level-detail-reports-tile-7d"
				/>
				<Tile
					label="Unique IPs"
					value={uniqueIpCount}
					testId="click-level-detail-reports-tile-ips"
				/>
			</section>

			{/* Chart */}
			<section
				className={`${CARD} px-5 py-4`}
				aria-label="Clicks over time"
			>
				<div className="mb-2 flex items-center justify-between">
					<p className="text-sm font-medium text-zinc-900">Clicks over time</p>
					<p className="text-xs text-zinc-500">
						{series.length > 0
							? `${series.length} day${series.length === 1 ? "" : "s"} in range`
							: ""}
					</p>
				</div>
				{series.length === 0 ? (
					<p
						className="py-6 text-sm text-zinc-500"
						data-testid="click-level-detail-reports-chart-empty"
					>
						No data to plot for the selected range.
					</p>
				) : (
					<Sparkline points={series} />
				)}
			</section>

			{/* Filter bar */}
			<form
				onSubmit={applyFilters}
				className={`${CARD} p-4`}
				aria-label="Drilldown filters"
				data-testid="click-level-detail-reports-filter-bar"
			>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
					<div>
						<label htmlFor="clrd-campaign" className={LABEL}>
							Campaign
						</label>
						<input
							id="clrd-campaign"
							type="text"
							placeholder="campaign_id"
							className={INPUT}
							value={campaign}
							onChange={(e) => setCampaign(e.target.value)}
							autoComplete="off"
							data-testid="click-level-detail-reports-campaign"
						/>
					</div>
					<div>
						<label htmlFor="clrd-country" className={LABEL}>
							Country (ISO)
						</label>
						<input
							id="clrd-country"
							type="text"
							placeholder="US, DE, IN…"
							className={INPUT}
							value={country}
							onChange={(e) => setCountry(e.target.value)}
							autoComplete="off"
							maxLength={8}
							data-testid="click-level-detail-reports-country"
						/>
					</div>
					<div className="flex items-end justify-end gap-2">
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={handleReset}
							data-testid="click-level-detail-reports-reset"
						>
							Reset
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							data-testid="click-level-detail-reports-apply-filters"
						>
							Apply filters
						</button>
					</div>
				</div>
			</form>

			{/* Drilldown table */}
			<section className={CARD} aria-label="Click drilldown">
				{listQuery.isLoading ? (
					<div
						className="px-6 py-8 text-sm text-zinc-500"
						data-testid="click-level-detail-reports-loading"
					>
						Loading clicks…
					</div>
				) : listQuery.isError ? (
					<div
						className="px-6 py-8 text-sm text-red-600"
						role="alert"
						data-testid="click-level-detail-reports-error"
					>
						Failed to load clicks: {listQuery.error.message}
					</div>
				) : rows.length === 0 ? (
					<div
						className="px-6 py-8 text-sm text-zinc-500"
						data-testid="click-level-detail-reports-empty"
					>
						No clicks match the selected range and filters. Widen the date
						range or clear a filter to see more results.
					</div>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-sm"
							data-testid="click-level-detail-reports-table"
						>
							<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-4 py-3 text-left font-medium">
										Timestamp (UTC)
									</th>
									<th className="px-4 py-3 text-left font-medium">IP</th>
									<th className="px-4 py-3 text-left font-medium">Country</th>
									<th className="px-4 py-3 text-left font-medium">User-Agent</th>
									<th className="px-4 py-3 text-left font-medium">Campaign</th>
									<th className="px-4 py-3 text-left font-medium">Referrer</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((r) => (
									<tr
										key={r.click_id}
										className="border-t border-zinc-200 transition-colors hover:bg-green-50/50"
										data-testid={`click-level-detail-reports-row-${r.click_id}`}
									>
										<td className="px-4 py-3 align-middle font-mono text-xs text-zinc-700">
											{formatUtcTs(r.ts)}
										</td>
										<td className="px-4 py-3 align-middle font-mono text-xs text-zinc-700">
											{r.ip ?? "—"}
										</td>
										<td className="px-4 py-3 align-middle">
											{r.country ? (
												<span className="inline-flex items-center rounded bg-green-50 px-1.5 py-0.5 font-mono text-xs text-green-700">
													{r.country.toUpperCase()}
												</span>
											) : (
												<span className="text-zinc-400">—</span>
											)}
										</td>
										<td className="px-4 py-3 align-middle text-xs text-zinc-700">
											<span title={r.ua ?? ""}>{truncate(r.ua, 48)}</span>
										</td>
										<td className="px-4 py-3 align-middle font-mono text-xs text-zinc-700">
											{r.campaign_id ?? "—"}
										</td>
										<td className="px-4 py-3 align-middle text-xs text-zinc-700">
											<span title={r.referrer ?? ""}>
												{truncate(r.referrer, 40)}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{rows.length > 0 && (
					<div className="flex items-center justify-between border-t border-zinc-200 px-4 py-3">
						<span className="text-xs text-zinc-500">
							Showing {rows.length.toLocaleString()} row
							{rows.length === 1 ? "" : "s"}
							{listQuery.data?.meta?.total != null
								? ` of ${listQuery.data.meta.total.toLocaleString()}`
								: ""}
						</span>
						<span className="text-xs text-zinc-400">
							Tenant-scoped via x-tenant-id
						</span>
					</div>
				)}
			</section>
		</div>
	);
}

export default ClickLevelDetailReportsPage;
